import { Injectable } from '@angular/core';
import {
  ListTableConfigResolver,
  ListTableDataLoader,
  ListTableSharedStateAdapter,
  ListTableDataContext,
  ListGridRequestPayload,
  ListConfigData,
  PPL_LIST_TABLE_CONFIG_RESOLVER,
  PPL_LIST_TABLE_DATA_LOADER,
  PPL_LIST_TABLE_SHARED_STATE
} from '@ppl/ui';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ListService } from '../services/list.service';
import { SharedDataService } from '../services/shared-data-service';

/** Converts boolean values in API response rows to "Yes"/"No" strings so column filters match the displayed text. */
function normalizeBooleans(response: any): any {
  const rows = response?.responsedata;
  if (!Array.isArray(rows)) return response;
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (typeof row[key] === 'boolean') {
        row[key] = row[key] ? 'Yes' : 'No';
      }
    }
  }
  return response;
}

/**
 * NextGen adapter for @ppl/ui list-table config resolution
 */
export function provideListConfigResolver() {
  return {
    provide: PPL_LIST_TABLE_CONFIG_RESOLVER,
    useFactory: (listService: ListService): ListTableConfigResolver => {
      return (pageConfig: string, jsonList: string): ListConfigData | null => {
        if (!pageConfig || !jsonList) {
          return null;
        }
        try {
          return listService.getListConfigData(pageConfig, jsonList);
        } catch (error) {
          console.error(`[NextGen] Failed to load config for ${pageConfig}/${jsonList}`, error);
          return null;
        }
      };
    },
    deps: [ListService]
  };
}

/**
 * NextGen adapter for @ppl/ui list-table data loading
 */
export function provideListDataLoader() {
  return {
    provide: PPL_LIST_TABLE_DATA_LOADER,
    useFactory: (listService: ListService): ListTableDataLoader => {
      return (payload: ListGridRequestPayload, context: ListTableDataContext): Observable<any> => {
        return listService.getListData(payload, context.from, context.hasUpdatedEntry).pipe(map(normalizeBooleans));
      };
    },
    deps: [ListService]
  };
}

/**
 * NextGen adapter for @ppl/ui list-table shared state
 */
export function provideListSharedState() {
  return {
    provide: PPL_LIST_TABLE_SHARED_STATE,
    useFactory: (sharedDataService: SharedDataService): ListTableSharedStateAdapter => {
      return {
        getData: (key: string) => sharedDataService.getData(key),
        setData: (key: string, value: any) => sharedDataService.setData(key, value),
        clearData: (key: string) => sharedDataService.clearData(key)
      };
    },
    deps: [SharedDataService]
  };
}

/**
 * Injectable adapter class that combines all list-table adapters.
 * Use this in component constructors for dependency injection.
 */
@Injectable()
export class NextgenListTableAdapter {
  constructor(
    public readonly listService: ListService,
    public readonly sharedDataService: SharedDataService
  ) {}

  getConfigResolver(): ListTableConfigResolver {
    return (pageConfig: string, jsonList: string): ListConfigData | null => {
      if (!pageConfig || !jsonList) {
        return null;
      }
      try {
        return this.listService.getListConfigData(pageConfig, jsonList);
      } catch (error) {
        console.error(`[NextGen] Failed to load config for ${pageConfig}/${jsonList}`, error);
        return null;
      }
    };
  }

  getDataLoader(): ListTableDataLoader {
    return (payload: ListGridRequestPayload, context: ListTableDataContext): Observable<any> => {
      return this.listService.getListData(payload, context.from, context.hasUpdatedEntry).pipe(map(normalizeBooleans));
    };
  }

  getSharedState(): ListTableSharedStateAdapter {
    return {
      getData: (key: string) => this.sharedDataService.getData(key),
      setData: (key: string, value: any) => this.sharedDataService.setData(key, value),
      clearData: (key: string) => this.sharedDataService.clearData(key)
    };
  }
}
